import DataGrid from "@components/DataGrid/DataGrid";
import i18n from "@i18n/i18n";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Tooltip,
  IconButton,
  Typography,
} from "@mui/material";
import { GridActionsCellItem, GridColDef, GridValidRowModel, GridRenderCellParams } from "@mui/x-data-grid-premium";
import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import useResizeObserver from "use-resize-observer";
import { useMetadataApisByType } from "@hooks/useMetadataApisByType";
import { useAddNonModelledFamilyType } from "@api/nonModelledFamily";
import { MetadataEntityUpdateRequest } from "@api/apiClient";
import AddModelTypeDialog from "./AddModelTypeDialog";
import EditBenchmarkModelTypeDialog from "./EditBenchmarkModelTypeDialog";
import BenchmarkCatalogDialog from "./BenchmarkCatalogDialog";

type GridMode = "catalog" | "tree" | "flat";

function buildGridRows(
  materials: any[],
  mode: GridMode,
  fallbackType: string,
): GridValidRowModel[] {
  if (mode === "catalog") {
    const result: GridValidRowModel[] = [];
    materials.forEach((catalog) => {
      const catalogId = catalog.metadataEntityId?.toString() ?? "";
      if (!catalogId) return;
      const values: any[] = (catalog.values ?? []).filter((v: any) => !v.isNew);
      result.push({
        id: catalogId,
        value: catalog.name ?? "",
        description: catalog.description ?? "",
        type: "BenchmarkCatalog",
        isInUse: catalog.isInUse ?? false,
        isParent: true,
        hasChildren: values.length > 0,
        level: 0,
        parentId: null,
      });
      values.forEach((v: any) => {
        result.push({
          id: v.metadataEntityId?.toString() ?? `${catalogId}-${v.scope}`,
          value:
            v.scope === "WholeBuilding"
              ? i18n.t("benchmark-catalog-dialog.scope-whole-building")
              : (v.modelTypeName ?? v.scope),
          description: `${v.valueKgCo2ePerM2 ?? 0} kgCO₂e/m²`,
          type: "BenchmarkCatalogValue",
          isParent: false,
          hasChildren: false,
          level: 1,
          parentId: catalogId,
        });
      });
    });
    return result;
  }

  if (mode === "tree") {
    const result: GridValidRowModel[] = [];
    materials.forEach((family: any) => {
      const familyId = family.metadataEntityId?.toString() || "";
      if (!familyId) return;
      const familyTypes = family.modelFamilyTypes || family.children || [];
      result.push({
        id: familyId,
        value: family.name,
        type: family.type,
        isParent: true,
        parentId: null,
        level: 0,
        hasChildren: familyTypes.length > 0,
      });
      familyTypes.forEach((familyType: any) => {
        const childId = familyType.metadataEntityId?.toString() || "";
        if (!childId) return;
        result.push({
          id: childId,
          value: familyType.name,
          type: familyType.type,
          isParent: false,
          parentId: familyId,
          level: 1,
          hasChildren: false,
        });
      });
    });
    return result;
  }

  return materials.map((item: any, index: number) => {
    if (item?.metadataEntityId !== undefined && item?.name !== undefined) {
      return {
        id: item.metadataEntityId.toString(),
        value: item.name,
        type: item.type || fallbackType,
        isInUse: item.isInUse ?? false,
        level: 0,
        isParent: false,
        hasChildren: false,
      };
    } else if (item?.id !== undefined && item?.name !== undefined) {
      return {
        id: item.id.toString(),
        value: item.name,
        type: fallbackType,
        isInUse: item.isInUse ?? false,
        level: 0,
        isParent: false,
        hasChildren: false,
      };
    } else {
      return {
        id: index.toString(),
        value: item.value || item,
        type: fallbackType,
        isInUse: false,
        level: 0,
        isParent: false,
        hasChildren: false,
      };
    }
  });
}

interface MetaDataGridProps {
  type: string;
  showModelTypeColumn: boolean;
}

const MetaDataGrid = observer(({ type, showModelTypeColumn }: MetaDataGridProps) => {
  const [rows, setRows] = useState<GridValidRowModel[]>([]);
  const [isAddTypeDialogOpen, setIsAddTypeDialogOpen] = useState(false);
  const [modelTypeInputs, setModelTypeInputs] = useState<string[]>(['']);
  const [selectedRowId, setSelectedRowId] = useState<string | number | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [duplicateErrors, setDuplicateErrors] = useState<Set<number>>(new Set());
  const containerRef = useRef(null);
  const { height } = useResizeObserver({ ref: containerRef });
  const { useData, useMetadataDelete, useMetadataUpdate } = useMetadataApisByType(type);
  const isBenchmarkModelType = type === "benchmark-model-type";
  const isBenchmarkCatalog = type === "benchmark-catalog";
  const isScenarioType = type === "scenario-type";

  const supportsInUse = isScenarioType || isBenchmarkModelType || isBenchmarkCatalog;
  const supportsInlineEdit = isScenarioType || isBenchmarkModelType;
  const supportsInfoIcon = isScenarioType || isBenchmarkModelType || isBenchmarkCatalog;

  const editConfirmMessage = isBenchmarkModelType
    ? i18n.t("rebar-material-grid.edit-benchmark-model-type-confirm")
    : isScenarioType
      ? i18n.t("rebar-material-grid.edit-scenario-type-confirm")
      : undefined;

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editRowId, setEditRowId] = useState<string | null>(null);
  const [editRowName, setEditRowName] = useState("");

  const [catalogEditId, setCatalogEditId] = useState<string | null>(null);
  const [catalogDialogOpen, setCatalogDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ message: string; severity: "success" | "error" | "warning" } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; value: string } | null>(null);

  const addFamilyTypeMutation = useAddNonModelledFamilyType({
    onSuccess: () => {
      console.log("Model family types added successfully");
      useData.refetch?.();
      handleCloseDialog();
    },
    onError: (error: any) => {
      console.error("Error adding model family types:", error);
      setSnackbar({ message: error.message || "Error adding model family types.", severity: "error" });
    }
  });

  const materials = (useData?.data ?? []) as any[];

  const toggleRowExpansion = (rowId: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  };

  const getExistingChildEntries = (parentId: string | number): string[] => {
    return rows
      .filter(row => row.parentId === parentId.toString() && !row.isParent)
      .map(row => row.value.toLowerCase().trim());
  };

  const validateDuplicates = (inputs: string[]): Set<number> => {
    const duplicateIndexes = new Set<number>();
    const existingEntries = selectedRowId ? getExistingChildEntries(selectedRowId) : [];
    
    inputs.forEach((input, index) => {
      const trimmedInput = input.toLowerCase().trim();
      
      if (trimmedInput === '') return; 
      if (existingEntries.includes(trimmedInput)) {
        duplicateIndexes.add(index);
        return;
      }
      
      
      const duplicateInInputs = inputs.some((otherInput, otherIndex) => 
        otherIndex !== index && 
        otherInput.toLowerCase().trim() === trimmedInput &&
        otherInput.trim() !== ''
      );
      
      if (duplicateInInputs) {
        duplicateIndexes.add(index);
      }
    });
    
    return duplicateIndexes;
  };

  const handleInputChange = (index: number, value: string) => {
    const newInputs = modelTypeInputs.map((input, i) => i === index ? value : input);
    setModelTypeInputs(newInputs);
    
    
    const duplicates = validateDuplicates(newInputs);
    setDuplicateErrors(duplicates);
  };

  const handleAddInput = () => {
    const newInputs = [...modelTypeInputs, ''];
    setModelTypeInputs(newInputs);
    
    const duplicates = validateDuplicates(newInputs);
    setDuplicateErrors(duplicates);
  };

  const handleRemoveInput = (index: number) => {
    if (modelTypeInputs.length > 1) {
      const newInputs = modelTypeInputs.filter((_, i) => i !== index);
      setModelTypeInputs(newInputs);
      
      const duplicates = validateDuplicates(newInputs);
      setDuplicateErrors(duplicates);
    }
  };

  useEffect(() => {
    const mode: GridMode = isBenchmarkCatalog ? "catalog" : showModelTypeColumn ? "tree" : "flat";
    setRows(buildGridRows(materials, mode, type));
    if (mode !== "catalog") setExpandedRows(new Set());
  }, [materials, type, showModelTypeColumn, isBenchmarkCatalog]);

  
  const visibleRows = (showModelTypeColumn || isBenchmarkCatalog)
    ? rows.filter(row => {
        if (row.isParent) return true;
        if (row.parentId && expandedRows.has(row.parentId)) return true;
        return false;
      })
    : rows;

  const handleDeleteClick = (params: any) => {
    const { id, value } = params.row;
    setDeleteTarget({ id: String(id), value: value ?? "" });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await useMetadataDelete.mutateAsync(deleteTarget);
      await useData.refetch?.();
    } catch (err: unknown) {
      const msg = (err as any)?.response?.data?.error || i18n.t("rebar-material-grid.delete-button.error-fallback", "Error deleting item.");
      setSnackbar({ message: msg, severity: "error" });
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteTarget(null);
  };

  const handleAddModelType = (rowId: string | number) => {
    setSelectedRowId(rowId);
    setModelTypeInputs(['']);
    setDuplicateErrors(new Set());
    setIsAddTypeDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsAddTypeDialogOpen(false);
    setModelTypeInputs(['']);
    setDuplicateErrors(new Set());
    setSelectedRowId(null);
  };

  const handleOpenEditDialog = (rowId: string, rowName: string) => {
    setEditRowId(rowId);
    setEditRowName(rowName ?? "");
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditRowId(null);
    setEditRowName("");
  };

  const editTypeMap: Record<string, string> = {
    "benchmark-model-type": "BenchmarkModelType",
    "scenario-type": "ScenarioType",
  };

  const handleSaveEdit = async (name: string) => {
    if (!editRowId) return;
    await useMetadataUpdate.mutateAsync(
      new MetadataEntityUpdateRequest({
        metadataEntityId: editRowId,
        name,
        type: editTypeMap[type] ?? type,
      })
    );
    await useData.refetch?.();
    setSnackbar({ message: i18n.t("rebar-material-grid.edit-success", { name }), severity: "success" });
  };

  const handleSaveModelType = async () => {
    if (selectedRowId === null) return;

    const modelTypes = modelTypeInputs.filter(value => value.trim() !== "");
    
    if (modelTypes.length === 0) {
      setSnackbar({ message: i18n.t("rebar-material-grid.add-model-type-dialog.validation.empty-list"), severity: "warning" });
      return;
    }

    const duplicates = validateDuplicates(modelTypeInputs);
    if (duplicates.size > 0) {
      setSnackbar({ message: i18n.t("rebar-material-grid.add-model-type-dialog.validation.duplicate-entries"), severity: "warning" });
      return;
    }

    try {  
      const payload = {
        ModelFamilyId: selectedRowId.toString(),
        ModelFamilyTypeList: modelTypes
      };      
      await addFamilyTypeMutation.mutateAsync(payload);      
    } catch (err) {
      console.error("Error adding model types:", err);      
    }
  };

  const TreeValueCell = ({ row }: GridRenderCellParams) => {
    const isExpanded = expandedRows.has(row.id.toString());
    return (
      <Box sx={{ display: "flex", alignItems: "center", pl: (row.level || 0) * 4, height: "100%" }}>
        {row.isParent && row.hasChildren && (
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); toggleRowExpansion(row.id.toString()); }}
            sx={{ p: 0.25, mr: 0.5 }}
          >
            {isExpanded
              ? <ExpandMoreIcon sx={{ fontSize: 16, color: "action.active" }} />
              : <ChevronRightIcon sx={{ fontSize: 16, color: "action.active" }} />}
          </IconButton>
        )}
        {(!row.hasChildren || !row.isParent) && <Box sx={{ width: 24, mr: 0.5 }} />}
        <Typography variant="body2" sx={{ color: "text.primary", flexGrow: 1 }}>
          {row.value}
        </Typography>
      </Box>
    );
  };

  const needsTree = showModelTypeColumn || isBenchmarkCatalog;

  const columns: GridColDef[] = (() => {
    const cols: GridColDef[] = [
      { field: "id", headerName: "ID", hideable: false },
      {
        field: "value",
        headerName: i18n.t("rebar-material-grid.columns.Name.value"),
        editable: false,
        flex: 1,
        sortable: !needsTree,
        ...(needsTree && { renderCell: (params: GridRenderCellParams) => <TreeValueCell {...params} /> }),
      },
    ];

    if (showModelTypeColumn) {
      cols.push({
        field: "add-model-type",
        headerName: i18n.t("rebar-material-grid.columns.Name.add-model-type"),
        width: 120,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          if (!params.row.isParent) return null;
          return (
            <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
              <Tooltip title={i18n.t("rebar-material-grid.add-model-type-button.tooltip")}>
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); handleAddModelType(params.row.id); }}
                  sx={{ padding: 2 }}
                  disabled={addFamilyTypeMutation.isPending}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      });
    }

    if (isBenchmarkCatalog) {
      cols.push({
        field: "description",
        headerName: i18n.t("benchmark-catalog-dialog.description-label"),
        flex: 1,
        editable: false,
      });
    }

    if (!isBenchmarkCatalog) {
      cols.push({
        field: "type",
        headerName: i18n.t("rebar-material-grid.columns.Name.type"),
        flex: 1,
        editable: false,
        sortable: !needsTree,
      });
    }

    cols.push({
      field: "actions",
      type: "actions",
      headerName: i18n.t("rebar-material-grid.columns.Name.action"),
      width: supportsInfoIcon ? 140 : 100,
      sortable: false,
      align: "left",
      headerAlign: "left",
      getActions: (params) => {
        if (isBenchmarkCatalog) {
          if (!params.row.isParent) return [];
          const catalogActions = [
            <GridActionsCellItem
              key={`info-catalog-${params.id}`}
              icon={
                <Tooltip title={i18n.t("rebar-material-grid.metadata-in-use-info")} placement="top">
                  <InfoOutlinedIcon color="action" />
                </Tooltip>
              }
              label={i18n.t("rebar-material-grid.metadata-in-use-info")}
              onClick={() => {}}
            />,
            <GridActionsCellItem
              key={`edit-catalog-${params.id}`}
              icon={
                <Tooltip title={i18n.t("rebar-material-grid.edit-button.tooltip")}>
                  <EditIcon />
                </Tooltip>
              }
              label={i18n.t("rebar-material-grid.edit-button.label")}
              onClick={() => {
                setCatalogEditId(String(params.id));
                setCatalogDialogOpen(true);
              }}
            />,
          ];
          if (!params.row.isInUse) {
            catalogActions.push(
              <GridActionsCellItem
                key={`delete-catalog-${params.id}`}
                icon={
                  <Tooltip title={i18n.t("rebar-material-grid.delete-button.tooltip")}>
                    <DeleteIcon />
                  </Tooltip>
                }
                label={i18n.t("rebar-material-grid.delete-button.label")}
                onClick={() => {
                  setDeleteTarget({ id: String(params.id), value: params.row.value ?? "" });
                }}
              />
            );
          }
          return catalogActions;
        }

        const actions = [];
        if (supportsInfoIcon) {
          actions.push(
            <GridActionsCellItem
              key={`info-${params.id}`}
              icon={
                <Tooltip title={i18n.t("rebar-material-grid.metadata-in-use-info")} placement="top">
                  <InfoOutlinedIcon color="action" />
                </Tooltip>
              }
              label={i18n.t("rebar-material-grid.metadata-in-use-info")}
              onClick={() => {}}
            />
          );
        }
        if (supportsInlineEdit) {
          actions.push(
            <GridActionsCellItem
              key={`edit-${params.id}`}
              icon={
                <Tooltip title={i18n.t("rebar-material-grid.edit-button.tooltip")}>
                  <EditIcon />
                </Tooltip>
              }
              label={i18n.t("rebar-material-grid.edit-button.label")}
              onClick={() => handleOpenEditDialog(String(params.row.id), params.row.value)}
            />
          );
        }
        if (supportsInUse ? !params.row.isInUse : true) {
          actions.push(
            <GridActionsCellItem
              key={`delete-${params.id}`}
              icon={
                <Tooltip title={i18n.t("rebar-material-grid.delete-button.tooltip")}>
                  <DeleteIcon />
                </Tooltip>
              }
              label={i18n.t("rebar-material-grid.delete-button.label")}
              onClick={() => handleDeleteClick(params)}
            />
          );
        }
        return actions;
      },
    });

    return cols;
  })();

  const columnVisibilityModel = {
    id: false,
  };

  return (
    <Box ref={containerRef} sx={{ width: "100%", height: "100%" }}>
      <DataGrid
        loading={useData?.isFetching || useData?.isPending}
        columns={columns}
        rows={visibleRows}
        disableColumnMenu
        disableRowSelectionOnClick
        disableColumnSorting={showModelTypeColumn || isBenchmarkCatalog}
        columnVisibilityModel={columnVisibilityModel}
        sx={{ height: `${(height || 400) - 16}px` }}
      />
      <AddModelTypeDialog
        open={isAddTypeDialogOpen}
        inputs={modelTypeInputs}
        onInputChange={handleInputChange}
        onAddInput={handleAddInput}
        onRemoveInput={handleRemoveInput}
        onClose={handleCloseDialog}
        onSave={handleSaveModelType}
        isSaving={addFamilyTypeMutation.isPending}
        duplicateErrors={duplicateErrors}
        selectedRowId={selectedRowId}
        getExistingChildEntries={getExistingChildEntries}
      />
      {supportsInlineEdit && editRowId && (
        <EditBenchmarkModelTypeDialog
          open={editDialogOpen}
          entityId={editRowId}
          currentName={editRowName}
          existingNames={rows
            .filter((r: GridValidRowModel) => String(r.id) !== editRowId)
            .map((r: GridValidRowModel) => (r.value as string) ?? "")}
          onClose={handleCloseEditDialog}
          onSave={handleSaveEdit}
          isSaving={useMetadataUpdate.isPending}
          confirmMessage={editConfirmMessage}
        />
      )}

      {/* Benchmark Catalog edit dialog (triggered from grid action) */}
      {isBenchmarkCatalog && (
        <BenchmarkCatalogDialog
          open={catalogDialogOpen}
          editCatalogId={catalogEditId}
          onClose={() => {
            setCatalogDialogOpen(false);
            setCatalogEditId(null);
          }}
          onSaved={() => {
            setCatalogDialogOpen(false);
            setCatalogEditId(null);
            setSnackbar({ message: i18n.t("benchmark-catalog-dialog.save-success"), severity: "success" });
          }}
        />
      )}

      <Dialog open={!!deleteTarget} onClose={handleDeleteCancel}>
        <DialogTitle>{i18n.t("rebar-material-grid.delete-dialog.title", "Confirm Delete")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {i18n.t("benchmark-catalog-dialog.confirm-delete")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} variant="outlined">
            {i18n.t("rebar-material-grid.delete-dialog.cancel", "Cancel")}
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" loading={useMetadataDelete.isPending}>
            {i18n.t("rebar-material-grid.delete-dialog.confirm", "Delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar?.severity ?? "success"} variant="filled" onClose={() => setSnackbar(null)}>
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
});

export default MetaDataGrid;